/**
 * Act 1, beat 2 — ***The Weakest Latch***.
 *
 * Spec §7: *"learn `focus`, watch the feeding round until the pattern is
 * legible, slip out during the 40 seconds the worker is at the far trough."*
 *
 * ── The rule this beat is written under ──────────────────────────────────────
 *
 * **The building is the antagonist, not the man.** He never looks for her. He
 * walks a round he has walked ten thousand times, in the same order, with the
 * same pauses, and the only reason it is dangerous is that it happens in a
 * straight lit hall she has to cross. Nothing in this beat is a puzzle the
 * player solves against an opponent — it is a timetable the player learns.
 *
 * That is also why the round runs whether or not the player does anything. A
 * round that waits for you is a lock; a round that keeps going is a world.
 *
 * ── Being seen is not being caught ───────────────────────────────────────────
 *
 * Spec §5.1's fairness rule: every detection is telegraphed and explainable
 * after the fact. If a playtester cannot say *why* they were seen, the encounter
 * is a bug, not a difficulty. Her breathing is the tell (there is no HUD, spec
 * §5.1), and the worker straightening up and turning is the other.
 *
 * And it costs her nothing but the window. Capture belongs to beat 4, which
 * needs a save system this beat deliberately does not have; here the worker
 * simply leaves early, the forty seconds stop being forty seconds, and she waits
 * for the next round. Soft failure, repeatable, no state to persist. A beat that
 * can only be failed by being made to replay it is a beat that respects the
 * player's time more than one that kills them.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `main.ts` decides what a cue means, exactly as in
 * `born.ts`. If a stage in this file ever imports Three, the beat has stopped
 * being testable in Node and the mistake will be here rather than in the
 * sequencer.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactable ids, shared between this beat and the world that places them.
 *
 * Exported as one object rather than as loose strings because a typo in an id is
 * silent in both directions — the stage waits for a completion that never
 * arrives, and the interactable is never waited on by anything. That is not a
 * theoretical worry; it is `BORN_IDS`' own comment, kept because it was earned.
 */
export const LATCH_IDS = {
  /**
   * The worker. Held `observe` — and he MOVES, which nothing in beat 1 did.
   *
   * The interaction system takes a static `at`, so the game re-points this one
   * every fixed step from his simulation position. See `InteractionSystem.moveTo`.
   */
  worker: 'latch.worker',
  /**
   * The drop-catch on her bay's gate. Accepts **both** verbs, deliberately.
   *
   * `nudge` rattles it and nothing else: no progress, no sound of it giving, just
   * a loose noise a solid gate would not make. That is the whole tutorial — the
   * player learns which gate is the weak one by touching it, not by being told.
   * `root` (held) is what actually works it off its keeper.
   *
   * Beat 1 taught `nudge` on something that answers and `root` on something
   * pleasant. This is the first object in the game that answers *differently* to
   * the two, and it is the beat's title made physical.
   */
  latch: 'latch.latch',
} as const;

/**
 * Story flags the GAME raises, as opposed to the ones the interaction system
 * completes on its own.
 *
 * All three are things only the simulation can know: where a worker is in his
 * round, how far a gate has swung, and where she is standing.
 */
export const LATCH_FLAGS = {
  /**
   * The worker has walked the outbound round and settled at the far trough.
   *
   * **Not "he has finished a round and left."** That was the first version and
   * it was 2 minutes 18 of a player having nothing to do: the return leg alone
   * is 28 seconds of watching a man walk away from you down a hall you have
   * already looked at.
   *
   * The pattern is legible at the moment he stops at the far end, and not one
   * second later — he came in, he worked up the row, and now he is a long way
   * off and staying there. That is the whole timetable, and everything after it
   * in the round is the player being asked to confirm something they have
   * already understood. Beat 1's three minutes are earned by things happening;
   * these would not have been.
   */
  roundLearned: 'latch.roundLearned',
  /** The gate has swung far enough that she can pass it. */
  gateOpen: 'latch.gateOpen',
  /** She has reached the hall door at the near end. */
  atDoor: 'latch.atDoor',
} as const;

/** Every cue this beat can emit. `main.ts` switches on these and nothing else. */
export const LATCH_CUES = {
  /** Morning in the bay: the lamp is on, her littermate is beside her. */
  open: 'latch.open',
  /** The walkway door opens at the near end and the round starts. */
  roundBegins: 'latch.roundBegins',
  /** He can be watched now. The `observe` prompt comes alive on him. */
  offerWorker: 'latch.offerWorker',
  /** She has watched a whole round. Nothing changes in the room; she knows it. */
  patternLearned: 'latch.patternLearned',
  /** The latch on her gate reads as a thing, and can be worked. */
  offerLatch: 'latch.offerLatch',
  /** The gate is open. Not a fanfare — a gate standing off its keeper. */
  gateGiven: 'latch.gateGiven',
  /** The round that matters. Forty seconds at the far trough begin. */
  windowOpens: 'latch.windowOpens',
  /** Fade to black. */
  fadeOut: 'latch.fadeOut',
  /** The beat is over. */
  end: 'latch.end',
} as const;

/**
 * How long the beat waits on a player who has not worked out a verb.
 *
 * Same reasoning and same number as beat 1's: spec §10 forbids softlocks, and
 * "the player never worked out that Q does something" is a softlock with better
 * manners. When a stage times out the verb is NOT taken away — the interactable
 * stays in the world and still works. The beat simply stops waiting.
 */
const PATIENCE = 45;

/**
 * The pause after the outbound round, with him still standing at the far end.
 *
 * Ten seconds in which nothing new happens and he is visibly a long way away.
 * That is the beat teaching distance: the player looks down twenty metres of lit
 * hall at a man who is not going anywhere, and works out on their own what that
 * is for. It is also the safety net — a player who never pressed `observe` gets
 * the pattern anyway here, because they did in fact watch the round, whatever
 * the input log says.
 */
const AFTER_ROUND = 10;

/** A stage that is purely a length of time with cues at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, patience: number, enterCue?: string): Stage {
  return {
    id,
    ...(enterCue === undefined ? {} : { enter: (_ctx, emit): void => emit(enterCue) }),
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

/** A stage that waits for a flag and will not give up, because the flag is the goal. */
function waitUntil(id: string, flag: string, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag),
  };
}

export const WEAKEST_LATCH: Beat = {
  number: 2,
  id: 'act1.weakestLatch',
  stages: [
    // ── settle ───────────────────────────────────────────────────────────────
    // No camera hold and no cutscene. Beat 1 earned the right to open in
    // gameplay: the player already knows this room, already knows the three
    // verbs, and already knows what happened here. Spending six seconds
    // re-establishing it would be telling them something they were present for.
    //
    // Six seconds is enough for the lamp and the littermate to register and not
    // enough to read as a wait.
    hold('settle', 6, LATCH_CUES.open),

    // ── the round ────────────────────────────────────────────────────────────
    // He comes through the walkway door and works his way up the hall. There is no
    // way to make this end sooner — the timetable is the antagonist and it does
    // not negotiate.
    //
    // No PATIENCE, because nothing here is waiting on the player. The worker's
    // own round is the clock, and `roundLearned` is raised by the simulation.
    waitUntil('round-1', LATCH_FLAGS.roundLearned, LATCH_CUES.roundBegins, LATCH_CUES.offerWorker),

    // ── learn focus ──────────────────────────────────────────────────────────
    // See AFTER_ROUND — this is where a player who watched and a player who did
    // not both arrive at the same place, which spec §10 requires and which costs
    // nothing, because they did both watch it happen.
    {
      ...waitFor('learn-focus', LATCH_IDS.worker, AFTER_ROUND),
      exit: (_ctx, emit): void => emit(LATCH_CUES.patternLearned),
    },

    // ── the latch ────────────────────────────────────────────────────────────
    // The title of the beat. She does not open a gate; she finds the one gate
    // that is weak — which is why the latch is offered only now, and why the
    // rattle (see LATCH_IDS.latch) has been available to anyone who touched it
    // since the beat began. The prompt is the game admitting what her snout
    // already found.
    //
    // PATIENCE here does NOT open the gate for her. It emits the offer again, in
    // effect, by letting the beat move to the window with the gate still shut —
    // and the window stage waits on `gateOpen` for ever, because getting out is
    // the beat. What patience buys is that the worker's next round starts on
    // schedule instead of the hall standing still, so a player who is slow to
    // find the latch does not also lose the rhythm they just learned.
    waitFor('the-latch', LATCH_FLAGS.gateOpen, PATIENCE, LATCH_CUES.offerLatch),

    // ── the window ───────────────────────────────────────────────────────────
    // No timeout, and that is deliberate rather than an oversight.
    //
    // Every other stage in this beat and in beat 1 has a patience clock, because
    // every other stage is waiting on a player to discover something. This one is
    // waiting on a player to *do* the thing the beat is about, and a timeout here
    // would mean the game finishing beat 2 on her behalf while she stood in the
    // pen — which is not mercy, it is the beat not happening.
    //
    // The softlock guarantee is therefore not a clock but a geometry proof: with
    // the gate open there is a route from her spawn, through the gate and down
    // the walkway, out of the walkway door — walked in `cold-spawn.spec.ts` (c),
    // which also holds that `atDoor` is not raised until she is through it
    // (session 70: it used to be true at spawn). If that goes red this stage
    // becomes a softlock.
    waitUntil('the-window', LATCH_FLAGS.atDoor, LATCH_CUES.windowOpens),

    // ── out ──────────────────────────────────────────────────────────────────
    {
      ...hold('close', 4, LATCH_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(LATCH_CUES.end),
    },
  ],
};
